// Castintech dashboard — Sol sidebar (Sirketler) UI modulu.
// Faz 9b PR8 — renderSidebar + selectCompany. Faz 10 PR1 — bindStaticNav.
// Faz 13 — strict TS hardening.
// Faz 15 — Mobile hamburger toggle (body.sidebar-open) + auto-close on nav click.

import { state } from '../state.js';
import { $, asEl } from '../utils.js';
import { navigate, refresh, render } from '../main.js';
import { renderStrip } from './topbar.js';
import type { PageId } from '../../types/state';

// Faz 10 PR1 — sidebar nav + sistem refresh statik click bind.
// Faz 15 — bindStaticNav ayni zamanda hamburger toggle + mobile auto-close baglar.
let _navBound = false;
const _NAV_PAGES: PageId[] = ['genel-bakis', 'client-os', 'nexus', 'hermes', 'pano', 'isler', 'ekip', 'canli', 'onaylar', 'beni-bekleyenler', 'skills', 'telegram', 'saglik', 'musteriler', 'mulkler', 'faturalar', 'kararlar', 'runtime-health', 'deploy-history', 'pilot-deploy-status', 'budget-status', 'agency-flow', 'workflow-trace', 'event-queue', 'api-keys', 'central-brain', 'whatsapp-demo', 'event-inbox', 'wiki-freshness', 'agent-skills', 'agent-council', 'cb-actions', 'cb-approvals', 'cb-lead-analyses', 'cb-conversations', 'cb-quote-drafts', 'org-chart', 'board', 'efqm', 'meetings', 'satisfaction', 'holding-kpi', 'credentials-expiry', 'design-jobs', 'autonomous-ops', 'crm-poc', 'crm-objects', 'aktif-isler', 'ceo-cockpit', 'agent-models', 'musteri-portallari'];

// Faz 15 — mobile breakpoint helper (CSS @media ile senkron, 768px).
function _isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth <= 768;
}

// Aşama D4 (2026-05-24) — sb-item <button> refactor sonrası basit nav binder.
// Önceki D1 yaklaşımı: <div>'e role+tabindex+keydown runtime ekle. D4'te HTML
// `<button class="sb-item" type="button">` oldu — native button Enter/Space
// otomatik tetikler, tabindex default 0. Sadece click handler + aria-label.
function _bindNavItem(el: HTMLElement, handler: () => void): void {
  // aria-label: text content (badge sayıları hariç) ilk satırı
  const label = (el.firstElementChild?.nextSibling?.textContent || el.textContent || '').trim();
  if (label && typeof el.hasAttribute === 'function' && !el.hasAttribute('aria-label')) {
    el.setAttribute('aria-label', label);
  }
  el.addEventListener('click', handler);
}

export function bindStaticNav(): void {
  if (_navBound) return;
  for (const p of _NAV_PAGES) {
    const el = $('nav-' + p);
    if (el) {
      _bindNavItem(el as HTMLElement, () => {
        navigate(p);
        // Faz 15 — Mobile'da nav item click sonrasi sidebar otomatik kapansin
        if (_isMobileViewport()) document.body.classList.remove('sidebar-open');
      });
    }
  }
  // Sistem > Yenile (sidebar bottom item — refresh-countdown sibling)
  const cd = $('refresh-countdown');
  const refreshItem = cd ? cd.closest<HTMLElement>('.sb-item') : null;
  if (refreshItem) _bindNavItem(refreshItem, () => refresh());

  // Faz 15 — Hamburger button: body.sidebar-open class'ini toggle et.
  const hamburger = $('sb-hamburger');
  if (hamburger) {
    hamburger.addEventListener('click', () => {
      document.body.classList.toggle('sidebar-open');
    });
    // Aşama D1 — hamburger zaten <button> ise role/tabindex gerek değil, sadece a11y label
    if (!hamburger.hasAttribute('aria-label')) {
      hamburger.setAttribute('aria-label', 'Menü');
      hamburger.setAttribute('aria-expanded', 'false');
    }
  }
  // Faz 15 — Backdrop click: sidebar'i kapat.
  const backdrop = $('sidebar-backdrop');
  if (backdrop) {
    backdrop.addEventListener('click', () => {
      document.body.classList.remove('sidebar-open');
    });
    // Backdrop sadece görsel — role=presentation
    backdrop.setAttribute('aria-hidden', 'true');
  }
  // Aşama D1 — Sidebar landmark
  const sidebarEl = document.querySelector('aside.sidebar');
  if (sidebarEl) {
    sidebarEl.setAttribute('role', 'navigation');
    sidebarEl.setAttribute('aria-label', 'Ana navigasyon');
  }

  _navBound = true;
}

export function renderSidebar(): void {
  const el = $('sb-companies');
  if (!el) return;
  const all = `<div class="sb-item ${state.selectedCompanyId === 'all' ? 'active' : ''}" data-action="select-company" data-company-id="all">
    <span class="sb-icon" style="font-size:10px">●</span> Tümü
  </div>`;
  const cos = state.companies.map(c => `
    <div class="sb-item ${state.selectedCompanyId === c.id ? 'active' : ''}" data-action="select-company" data-company-id="${c.id}">
      <span class="sb-company-dot ${state.selectedCompanyId === c.id ? 'active' : 'inactive'}"></span>
      <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:12px;">${c.name}</span>
    </div>`).join('');
  el.innerHTML = all + cos;

  // Approval badge
  const pend = state.approvals.filter(a => a.status === 'pending').length;
  const badge = $('approval-badge');
  if (badge) {
    if (pend > 0) {
      badge.style.display = '';
      badge.textContent = String(pend);
    } else {
      badge.style.display = 'none';
    }
  }
}

export function selectCompany(id: string): void {
  state.selectedCompanyId = id;
  state.agentFilter = null;
  // Faz 50.A — Filter persist localStorage.
  try { localStorage.setItem('castintech_selected_company', id); } catch { /* private mode */ }
  renderSidebar();
  renderStrip();
  render();
}

// Faz 10 PR5 — Sidebar companies delegated click.
let _sidebarDelegated = false;
export function bindSidebarDelegation(): void {
  if (_sidebarDelegated) return;
  const el = $('sb-companies');
  if (!el) return;
  el.addEventListener('click', (e: MouseEvent) => {
    const item = asEl(e.target)?.closest<HTMLElement>('[data-action="select-company"]');
    if (!item || !el.contains(item)) return;
    const id = item.dataset.companyId;
    if (id) selectCompany(id);
  });
  _sidebarDelegated = true;
}
